"use client";

import { useEffect } from "react";

import { useFaqDrawer } from "@/lib/faq-context";
import { faqAnchor, type FaqContent, type FaqStory } from "@/lib/faqs";

type BridgeStory = {
  uuid?: string;
  name?: string;
  slug?: string;
  full_slug?: string;
  content?: FaqContent;
};
type StoryblokInputEvent = { story?: BridgeStory };
type StoryblokBridgeInstance = {
  on: (events: string | string[], cb: (payload?: StoryblokInputEvent) => void) => void;
};
type StoryblokBridgeWindow = Window & {
  StoryblokBridge?: new (opts?: object) => StoryblokBridgeInstance;
};

export default function FaqStoryPreview({ initialStory }: { initialStory: FaqStory }) {
  const { upsertFaq } = useFaqDrawer();
  const anchor = faqAnchor(initialStory.slug);

  useEffect(() => {
    upsertFaq(initialStory);
  }, [upsertFaq, initialStory]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.location.hash !== `#${anchor}`) {
      window.location.hash = anchor;
    }
  }, [anchor]);

  useEffect(() => {
    const w = window as StoryblokBridgeWindow;
    if (!w.StoryblokBridge) return;
    const bridge = new w.StoryblokBridge();
    bridge.on(["input", "change", "published"], (event) => {
      const story = event?.story;
      if (!story?.uuid || !story?.content) return;
      upsertFaq({
        uuid: story.uuid,
        name: story.name ?? initialStory.name,
        slug: story.full_slug ?? story.slug ?? initialStory.slug,
        content: story.content,
      });
    });
  }, [upsertFaq, initialStory.name, initialStory.slug]);

  return null;
}
